import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useArtifactoryStore } from '../../stores/artifactoryStore';
import { useHydrationStore } from '../../stores/hydrationStore';
import { NodeStatus } from '../../types/artifactory';
import Editor from '@monaco-editor/react';
import { useTheme } from '../../contexts/ThemeContext';
import { FiCode, FiSettings, FiPlay, FiRefreshCw, FiFileText, FiDownload, FiArrowLeft, FiAlertTriangle } from 'react-icons/fi';
import api from '../../services/api';
import PersonaSelector from '../common/PersonaSelector';
import { ArtifactoryMarkdown } from './ArtifactoryMarkdown';
import { ExportButton } from './ExportButton';
import { HandoffButton } from './HandoffButton';
import { HydrationPlanPanel } from './HydrationPlanPanel';

// Create a new component for fetching and displaying artifact content
const ArtifactContent: React.FC<{ 
  artifactId: string, 
  filePath: string,
  contentType?: 'code' | 'documentation'
}> = ({ 
  artifactId, 
  filePath,
  contentType = 'code'
}) => {
  const [content, setContent] = useState<string>("// Loading content...");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { darkMode } = useTheme();
  
  useEffect(() => {
    const loadContent = async () => {
      setIsLoading(true);
      setError(null);
      
      // console.log(`Loading artifact content for ID: ${artifactId}`);
      
      try {
        const response = await api.get(`/artifacts/${artifactId}`);
        // console.log(`Artifact content loaded successfully for ${artifactId}`);
        
        if (contentType === 'documentation') {
          // For documentation, we need to check the metadata
          const metadata = response.data.metadata || {};
          const rationale = metadata.rationale || '';
          const notes = metadata.notes || '';
          
          if (rationale || notes) {
            let docContent = '';
            
            if (rationale) {
              docContent += `## Design Rationale\n\n${rationale}\n\n`;
            }
            
            if (notes) {
              docContent += `## Implementation Notes\n\n${notes}\n\n`;
            }
            
            setContent(docContent);
          } else if (metadata.full_response) {
            // If we have the full response but no parsed sections, try to extract them
            const fullResponse = metadata.full_response;
            let docContent = '';
            
            // Try to extract rationale
            const rationaleMatch = /(?:<RATIONALE>|\n# Design Rationale\n)([\s\S]*?)(?:<\/RATIONALE>|(?=<CODE>|\n# Implementation))/i.exec(fullResponse);
            if (rationaleMatch && rationaleMatch[1].trim()) {
              docContent += `## Design Rationale\n\n${rationaleMatch[1].trim()}\n\n`;
            }
            
            // Try to extract notes
            const notesMatch = /(?:<NOTES>|\n# Implementation Notes\n)([\s\S]*?)(?:<\/NOTES>|$)/i.exec(fullResponse);
            if (notesMatch && notesMatch[1].trim()) {
              docContent += `## Implementation Notes\n\n${notesMatch[1].trim()}\n\n`;
            }
            
            if (docContent) {
              setContent(docContent);
            } else {
              setContent("// No documentation available for this file.");
            }
          } else {
            setContent("// No documentation available for this file.");
          }
        } else {
          // For code, just use the content directly
          setContent(response.data.content);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to load artifact content:", error);
        setError(error.message || "Unknown error");
        setContent("// Error loading content: " + (error.message || "Unknown error"));
        setIsLoading(false);
      }
    };
    
    if (artifactId) {
      loadContent();
    } else {
      setContent("// No artifact ID provided");
      setIsLoading(false);
    }
  }, [artifactId, contentType]);
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p>Loading content...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center text-red-500">
          <p className="mb-2">Error loading content:</p>
          <p className="font-mono text-sm">{error}</p>
        </div>
      </div>
    );
  }
  
  return contentType === 'documentation' ? (
    <div className="p-4 overflow-y-auto h-full">
      <ArtifactoryMarkdown content={content} />
    </div>
  ) : (
    <Editor
      height="100%"
      defaultLanguage={getLanguageFromPath(filePath)}
      theme={darkMode ? "vs-dark" : "light"}
      value={content}
      options={{
        readOnly: true,
        minimap: { enabled: true },
        scrollBeyondLastLine: false,
        fontSize: 14,
        wordWrap: 'on'
      }}
    />
  );
};

// Define view types for better type safety
type ViewType = 'blueprint' | 'code' | 'documentation' | 'hydration-plan';

// Interface for the node inspector state
interface NodeInspectorState {
  isHydrating: boolean;
  hydrationInProgress: boolean;
}

export const NodeInspector: React.FC<{ sessionId?: string }> = ({ sessionId }) => {
  // Centralized state management
  const [state, setState] = useState<NodeInspectorState>({
    isHydrating: false,
    hydrationInProgress: false
  });
  
  const { 
    manifest, 
    selectedNodeId, 
    updateNodeLocal, 
    saveNodeConfig, 
    hydrateNode,
    activeView,
    setActiveView
  } = useArtifactoryStore();
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  // Get the selected node
  const node = selectedNodeId && manifest ? manifest.nodes[selectedNodeId] : null;
  
  // Computed properties
  const showHydrationPlan = activeView === 'hydration-plan';

  // Debug logging for node status - disabled to reduce console noise
  /*
  useEffect(() => {
    if (node) {
      console.log(`Node ${node.id} status: ${node.status}, artifact_id: ${node.artifact_id || 'none'}`);
    }
  }, [node]);
  */

  // Auto-switch to code tab when node is hydrated - but only if we're not in hydration-plan view
  useEffect(() => {
    if (node && node.status === 'fresh' && node.artifact_id && 
        activeView !== 'hydration-plan' && 
        activeView !== 'code' && 
        activeView !== 'blueprint' &&
        activeView !== 'documentation') { // Don't auto-switch from documentation tab
      // console.log(`Node ${node.id} is fresh with artifact_id ${node.artifact_id}, switching to code tab`);
      setActiveView('code');
    }
  }, [node?.status, node?.artifact_id, activeView, setActiveView]);
  
  // Reset hydration progress when node changes
  useEffect(() => {
    setState(prev => ({ 
      ...prev, 
      hydrationInProgress: false
    }));
    // Note: We no longer reset activeView here to maintain view persistence
  }, [selectedNodeId]);

  // Handle view switching
  const switchView = (view: ViewType) => {
    // console.log(`Switching from ${activeView} view to ${view} view`);
    setActiveView(view);
  };

  // Log component mount/unmount - disabled to reduce console noise
  /*
  useEffect(() => {
    console.log(`NodeInspector mounted with selectedNodeId: ${selectedNodeId || 'none'}`);
    return () => {
      console.log('NodeInspector unmounted');
    };
  }, [selectedNodeId]);
  */
  
  // Log state changes - disabled to reduce console noise
  /*
  useEffect(() => {
    console.log(`NodeInspector state changed:`, state);
  }, [state]);
  */

// Get hydration store at component level
const hydrationStore = useHydrationStore();

// Handle hydration
const handleHydrate = async (withDependencies: boolean = false) => {
  if (!node || !manifest || !sessionId) return;
  
  // Special handling for directory nodes
  if (node.type === 'directory') {
    setState(prev => ({ ...prev, isHydrating: true, hydrationInProgress: true }));
    
    try {
      // For directories, always use the hydration plan view to show contained files
      await hydrationStore.initialize(manifest.id, sessionId, node.id);
      
      // Switch to hydration plan view
      setState(prev => ({ 
        ...prev, 
        activeView: 'hydration-plan',
        hydrationInProgress: false
      }));
    } catch (error) {
      console.error("Error during directory hydration setup:", error);
      setState(prev => ({ ...prev, hydrationInProgress: false }));
    } finally {
      setState(prev => ({ ...prev, isHydrating: false }));
    }
    return;
  }
  
  // Regular handling for file nodes
  setState(prev => ({ ...prev, isHydrating: true, hydrationInProgress: true }));
  
  try {
    console.log(`Starting hydration for node ${node.id} ${withDependencies ? 'WITH' : 'WITHOUT'} dependencies`);
    
    if (withDependencies) {
      // Initialize the hydration store with the current node as focus
      console.log(`Initializing hydration store with focus node: ${node.id}`);
      await hydrationStore.initialize(manifest.id, sessionId, node.id);
      
      // Switch to hydration plan view
      setState(prev => ({ 
        ...prev, 
        activeView: 'hydration-plan',
        hydrationInProgress: false
      }));
    } else {
      // For single node hydration, use the existing method
      await hydrateNode(node.id, sessionId, false);
      
      // Switch to code view
      setState(prev => ({ 
        ...prev, 
        activeView: 'code',
        hydrationInProgress: false
      }));
    }
  } catch (error) {
    console.error("Error during hydration:", error);
    setState(prev => ({ ...prev, hydrationInProgress: false }));
  } finally {
    setState(prev => ({ ...prev, isHydrating: false }));
  }
};

  // If no node is selected
  if (!node) {
    return (
      <div className={`h-full flex items-center justify-center ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
        <div className="text-center">
          <FiCode className="mx-auto text-4xl mb-4 text-gray-400" />
          <p className="text-lg">Select a file to view or edit</p>
        </div>
      </div>
    );
  }
  
  // Special view for root node
  const isRootNode = node.path === '/' || node.path === '' || node.path === '.';
  console.log("NodeInspector - node:", node);
  console.log("NodeInspector - isRootNode:", isRootNode, "path:", node.path);
  
  if (isRootNode && manifest) {
    console.log("Rendering root node special view");
    return (
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className={`py-1 px-2 border-b flex justify-between items-center z-10 flex-shrink-0 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold">Project Tools</h2>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Export Section */}
          <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-750 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <FiDownload className="mr-2 text-green-500" />
              Export Project
            </h3>
            <p className="mb-4 text-sm">Export your project as a ZIP file with all generated code files.</p>
            <div className="flex gap-2">
              <ExportButton sessionId={sessionId} manifestId={manifest.id} />
              <HandoffButton manifestId={manifest.id} />
            </div>
          </div>
          
          {/* Hydration Plan Section */}
          <div className={`p-4 rounded-lg border ${darkMode ? 'bg-gray-750 border-gray-700' : 'bg-white border-gray-200'}`}>
            <h3 className="text-lg font-semibold mb-3 flex items-center">
              <FiRefreshCw className="mr-2 text-blue-500" />
              Project Hydration
            </h3>
            <p className="mb-4 text-sm">Manage the hydration status of your project files.</p>
            <HydrationPlanPanel
              manifestId={manifest.id}
              sessionId={sessionId}
              maxHeight="400px"
              // Don't pass focusNodeId for the root node view
              // This ensures we show all nodes in the project
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header - with reduced height and sticky positioning */}
      <div className={`py-1 px-2 border-b flex justify-between items-center z-10 flex-shrink-0 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold font-mono break-words max-w-full">{node.path}</h2>
          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Status: <span className={getStatusColor(node.status)}>{node.status}</span>
            {node.artifact_id && <span className="ml-2 text-green-500">• Artifact ID: {node.artifact_id.substring(0, 8)}...</span>}
          </div>
        </div>
        <div className="flex flex-col space-y-1 flex-shrink-0">
          <div className="flex space-x-2">
            <button
              className={`px-3 py-1 rounded text-xs font-medium shadow-sm ${
                activeView === 'blueprint' 
                  ? 'bg-blue-600 text-white' 
                  : darkMode 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
              onClick={() => switchView('blueprint')}
            >
              <FiSettings className="inline mr-1" />
              Blueprint
            </button>
            <button
              className={`px-3 py-1 rounded text-xs font-medium shadow-sm ${
                activeView === 'code' 
                  ? 'bg-blue-600 text-white' 
                  : node.artifact_id
                    ? darkMode 
                      ? 'bg-green-700 hover:bg-green-600 text-white' 
                      : 'bg-green-200 hover:bg-green-300 text-green-800'
                    : darkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
              onClick={() => switchView('code')}
            >
              <FiCode className="inline mr-1" />
              Code {node.artifact_id ? '✓' : ''}
            </button>
            
            <button
              className={`px-3 py-1 rounded text-xs font-medium shadow-sm ${
                activeView === 'documentation' 
                  ? 'bg-purple-600 text-white' 
                  : node.artifact_id
                    ? darkMode 
                      ? 'bg-purple-700 hover:bg-purple-600 text-white' 
                      : 'bg-purple-200 hover:bg-purple-300 text-purple-800'
                    : darkMode 
                      ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
              onClick={() => switchView('documentation')}
            >
              <FiFileText className="inline mr-1" />
              Docs
            </button>
          </div>
          
          {/* Hydration button */}
          <div className="flex space-x-2">
            <button
              className={`px-3 py-1 rounded text-xs font-medium shadow-sm ${
                state.isHydrating
                  ? 'bg-gray-600 cursor-not-allowed'
                  : activeView === 'hydration-plan'
                    ? 'bg-blue-600 text-white'
                    : node.status === 'ghost'
                      ? darkMode ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-amber-500 hover:bg-amber-600 text-white'
                      : darkMode ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'bg-orange-500 hover:bg-orange-600 text-white'
              }`}
              onClick={() => {
                if (activeView === 'hydration-plan') {
                  // If already in hydration plan view, switch back to previous view
                  const previousView = localStorage.getItem('artifactory_previousView') as ViewType || 'blueprint';
                  switchView(previousView);
                } else {
                  // Store current view before switching to hydration plan
                  localStorage.setItem('artifactory_previousView', activeView);
                  // Switch to hydration plan view
                  switchView('hydration-plan');
                }
              }}
              disabled={state.isHydrating}
            >
              {state.isHydrating ? (
                <>
                  <FiRefreshCw className="inline animate-spin mr-1" />
                  Hydrating...
                </>
              ) : activeView === 'hydration-plan' ? (
                <>
                  <FiArrowLeft className="inline mr-1" />
                  Back
                </>
              ) : node.status === 'ghost' ? (
                <>
                  <FiPlay className="inline mr-1" />
                  Hydrate
                </>
              ) : (
                <>
                  <FiRefreshCw className="inline mr-1" />
                  Rehydrate
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Content - hydration-plan gets its own non-scrolling container */}
      {activeView === 'hydration-plan' ? (
        <div className="flex-1 min-h-0 overflow-hidden">
          <div className="h-full flex flex-col overflow-hidden" data-testid="hydration-plan-container">
            {manifest?.id ? (
              <HydrationPlanPanel
                key={`hydration-plan-${node.id}-${manifest.id}`}
                manifestId={manifest.id}
                sessionId={sessionId || ''}
                focusNodeId={node.id}
                maxHeight="100%"
                onComplete={() => {
                  hydrateNode(node.id, sessionId || '', false);
                }}
                forceRender={false}
                onError={(error) => {
                  console.error("Hydration plan error:", error);
                  if (sessionId && error.response?.status === 404) {
                    console.log("Attempting to recover from 404 error with session ID:", sessionId);
                  }
                }}
              />
            ) : (
              <div className={`p-4 rounded-md ${darkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-red-100 text-red-700'}`}>
                <h2 className="text-lg font-semibold mb-2">Error</h2>
                <p>Manifest ID not available. Cannot load hydration plan.</p>
              </div>
            )}
          </div>
        </div>
      ) : (
      <div className="flex-1 overflow-y-auto min-h-0 h-0">
        {(() => {
          // console.log(`Rendering content for view: ${activeView}`);
          
          switch (activeView) {
            case 'documentation':
              return node.artifact_id ? (
                <div className="p-4 space-y-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Documentation for {node.path.split('/').pop()}</h3>
                  </div>
                  
                  <ArtifactContent 
                    artifactId={node.artifact_id} 
                    filePath={node.path} 
                    contentType="documentation" 
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 italic">
                  No documentation available. Generate code first.
                </div>
              );
              
            case 'blueprint':
              return (
                <div className="p-4 space-y-4">
                  <div>
                    <label className={`block text-xs font-bold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Assigned Persona
                    </label>
                    <PersonaSelector
                      selectedPersonaId={node.assigned_persona_id}
                      onSelectPersona={(personaId) => {
                        updateNodeLocal(node.id, { assigned_persona_id: personaId });
                        saveNodeConfig(node.id);
                      }}
                      darkMode={darkMode}
                    />
                    <button
                      onClick={() => {
                        const content = `Review artifact at ${node.path} generated by ${node.assigned_persona_id}`;
                        navigate('/personas/forge', { state: { sendTo: { content, action: 'mutate' } } });
                      }}
                      className={`mt-2 w-full px-3 py-1.5 text-xs rounded transition-colors ${
                        darkMode
                          ? 'bg-purple-600/20 text-purple-300 hover:bg-purple-600/30'
                          : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                      }`}
                    >
                      Send to Forge for Review
                    </button>
                  </div>
                  
                  <div>
                    <label className={`block text-xs font-bold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Dependencies
                    </label>
                    <div className={`rounded p-2 max-h-40 overflow-y-auto ${
                      darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-300'
                    } border`}>
                      {node.dependencies.length > 0 ? (
                        node.dependencies.map(depId => (
                          <div key={depId} className="text-xs font-mono mb-1">
                            {manifest?.nodes[depId]?.path || depId}
                          </div>
                        ))
                      ) : (
                        <div className="text-xs italic">No dependencies</div>
                      )}
                    </div>
                  
                    {/* 
                    // Path-based dependencies warning - hidden from users
                    // Uncomment if we want to show this in the future
                    {node.dependencies.some(dep => dep.includes('/') && !manifest?.nodes[dep]) && (
                      <div className={`mt-2 p-2 rounded ${darkMode ? 'bg-yellow-900 bg-opacity-20' : 'bg-yellow-50'}`}>
                        <div className="flex items-center text-yellow-500">
                          <FiAlertTriangle className="mr-1" />
                          <span className="font-medium">Path-based dependencies detected</span>
                        </div>
                        <p className="text-xs mt-1">
                          Some dependencies are specified as paths rather than node IDs. This may cause issues during hydration.
                          Consider hydrating this node with dependencies to resolve these references.
                        </p>
                        <button
                          className={`mt-2 px-2 py-1 text-xs rounded ${
                            darkMode ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                          }`}
                          onClick={() => handleHydrate(true)}
                        >
                          Hydrate with Dependencies
                        </button>
                      </div>
                    )}
                    */}
                  </div>
                  
                  <div>
                    <label className={`block text-xs font-bold mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Generation Prompt
                    </label>
                    <textarea
                      className={`w-full h-32 rounded p-2 text-sm ${
                        darkMode 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-800'
                      } border`}
                      value={node.generation_prompt}
                      onChange={(e) => updateNodeLocal(node.id, { generation_prompt: e.target.value })}
                      onBlur={() => saveNodeConfig(node.id)}
                    />
                  </div>
                  
                  {/* Dependencies info */}
                  {node.dependencies.length > 0 && (
                    <div className={`mt-2 text-xs text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      This node has {node.dependencies.length} dependencies
                    </div>
                  )}
                </div>
              );
              
            case 'hydration-plan':
              return null; // handled outside the scroll container above
            case '__hydration-plan-old-dead-code__':
              return (
              <div className="h-full p-4" data-testid="hydration-plan-container-dead">
                {manifest?.id ? (
                  <HydrationPlanPanel
                    key={`hydration-plan-${node.id}-${manifest.id}`} // Use manifest.id in key instead of Date.now()
                    manifestId={manifest.id}
                    sessionId={sessionId || ''}
                    focusNodeId={node.id}
                    maxHeight="calc(100vh - 200px)"
                    onComplete={() => {
                      // console.log("Hydration plan complete, refreshing node");
                      // Refresh the node after hydration is complete
                      hydrateNode(node.id, sessionId || '', false);
                    }}
                    forceRender={false} // Set to false to prevent re-renders
                    onError={(error) => {
                      // Don't automatically navigate away on error - let the user decide
                      console.error("Hydration plan error:", error);
                    
                      // If we have a session ID but manifest ID is wrong, try to get the correct one
                      if (sessionId && error.response?.status === 404) {
                        console.log("Attempting to recover from 404 error with session ID:", sessionId);
                      }
                    }}
                  />
                ) : (
                  <div className={`p-4 rounded-md ${darkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-red-100 text-red-700'}`}>
                    <h2 className="text-lg font-semibold mb-2">Error</h2>
                    <p>Manifest ID not available. Cannot load hydration plan.</p>
                    {sessionId && (
                      <button
                        onClick={async () => {
                          try {
                            console.log("Attempting to find manifest ID for session:", sessionId);
                            const sessionManifestInfo = await artifactoryService.getSessionManifest(sessionId);
                            if (sessionManifestInfo.has_manifest && sessionManifestInfo.manifest_id) {
                              console.log("Found manifest ID:", sessionManifestInfo.manifest_id);
                              // Refresh the page with the correct manifest ID
                              window.location.href = `/artifactory/${sessionId}?manifestId=${sessionManifestInfo.manifest_id}`;
                            }
                          } catch (error) {
                            console.error("Error finding manifest ID:", error);
                          }
                        }}
                        className={`mt-2 px-3 py-1 rounded text-sm ${
                          darkMode ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                        }`}
                      >
                        Attempt Recovery
                      </button>
                    )}
                  </div>
                )}
              </div>
            ); 
            
            case 'code':
              return node.artifact_id ? (
                <React.Suspense fallback={<div className="flex items-center justify-center h-full">Loading content...</div>}>
                  {/* console.log("Rendering artifact content view") */}
                  <ArtifactContent artifactId={node.artifact_id} filePath={node.path} />
                </React.Suspense>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 italic">
                  GHOST STATE: No code generated yet.
                </div>
              );
            
            default:
              return null;
          }
        })()}
        
        {/* Error information display */}
        {node.metadata?.last_error && activeView !== 'hydration-plan' && (
          <div className={`absolute bottom-0 left-0 right-0 p-3 ${darkMode ? 'bg-red-900 bg-opacity-20' : 'bg-red-50'}`}>
            <h3 className={`text-sm font-semibold ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
              Last Error
            </h3>
            <p className="text-xs mt-1">
              {node.metadata.last_error}
            </p>
            {node.metadata.last_error_timestamp && (
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {new Date(node.metadata.last_error_timestamp).toLocaleString()}
              </p>
            )}
            <button
              className={`mt-2 px-2 py-1 text-xs rounded ${
                darkMode ? 'bg-red-800 hover:bg-red-700 text-white' : 'bg-red-100 hover:bg-red-200 text-red-800'
              }`}
              onClick={() => handleHydrate(false)}
            >
              Retry Hydration
            </button>
          </div>
        )}
      </div>
      )}{/* end hydration-plan conditional */}
    </div>
  );
};

// Helper function to get status color
const getStatusColor = (status: string) => {
  switch (status) {
    case NodeStatus.GHOST: return 'text-gray-400';
    case NodeStatus.PENDING: return 'text-yellow-500';
    case NodeStatus.HYDRATING: return 'text-blue-500';
    case NodeStatus.FRESH: return 'text-green-500';
    case NodeStatus.STALE: return 'text-orange-500';
    case NodeStatus.LOCKED: return 'text-red-500';
    default: return 'text-gray-400';
  }
};

// Helper function to determine language from file path
const getLanguageFromPath = (path: string): string => {
  const extension = path.split('.').pop()?.toLowerCase();
  
  switch (extension) {
    case 'js': return 'javascript';
    case 'jsx': return 'javascript';
    case 'ts': return 'typescript';
    case 'tsx': return 'typescript';
    case 'py': return 'python';
    case 'html': return 'html';
    case 'css': return 'css';
    case 'json': return 'json';
    case 'md': return 'markdown';
    default: return 'plaintext';
  }
};
